import React, { useEffect, useState, useCallback } from "react";
import api, { resolveImage, formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Loader2, Eye } from "lucide-react";

const STATUSES = ["New", "Confirmed", "Processing", "Shipped", "Delivered", "Cancelled"];
const statusColor = {
  New: "bg-blue-100 text-blue-700", Confirmed: "bg-indigo-100 text-indigo-700",
  Processing: "bg-amber-100 text-amber-700", Shipped: "bg-purple-100 text-purple-700",
  Delivered: "bg-green-100 text-green-700", Cancelled: "bg-red-100 text-red-700",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (status !== "All") params.status = status;
    if (search) params.search = search;
    try {
      const { data } = await api.get("/admin/orders", { params });
      setOrders(data);
    } catch { setOrders([]); }
  }, [status, search]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      const { data } = await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Order marked ${newStatus}`);
      setOrders((os) => os.map((o) => (o.id === orderId ? data : o)));
      if (selected?.id === orderId) setSelected(data);
    } catch { toast.error("Failed to update status"); }
  };

  return (
    <div data-testid="admin-orders-page">
      <h1 className="font-serif text-3xl lg:text-4xl text-obsidian mb-1">Orders</h1>
      <p className="text-sm text-muted-foreground mb-6">{orders?.length || 0} orders</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search order # or customer..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-sm bg-white" data-testid="order-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-48 rounded-sm bg-white" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {orders === null ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gold" /></div>
      ) : (
        <div className="bg-white border border-black/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-4 font-semibold">Order</th>
                <th className="p-4 font-semibold hidden md:table-cell">Customer</th>
                <th className="p-4 font-semibold hidden sm:table-cell">Date</th>
                <th className="p-4 font-semibold">Total</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold text-right">View</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-black/5 last:border-0 hover:bg-secondary/40" data-testid={`admin-order-${o.order_number}`}>
                  <td className="p-4 font-semibold text-obsidian">#{o.order_number}</td>
                  <td className="p-4 hidden md:table-cell">
                    <p className="text-obsidian">{o.customer.name}</p>
                    <p className="text-xs text-muted-foreground">{o.customer.mobile}</p>
                  </td>
                  <td className="p-4 hidden sm:table-cell text-muted-foreground text-xs">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
                  <td className="p-4 font-bold">{formatINR(o.total)}</td>
                  <td className="p-4">
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                      <SelectTrigger className="w-36 h-8 rounded-sm text-xs" data-testid={`status-select-${o.order_number}`}>
                        <Badge className={`${statusColor[o.status]} border-0 rounded-sm`}>{o.status}</Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-right">
                    <Dialog>
                      <DialogTrigger asChild>
                        <button onClick={() => setSelected(o)} className="p-2 hover:bg-secondary rounded-sm transition-colors" data-testid={`view-order-${o.order_number}`}><Eye className="w-4 h-4" /></button>
                      </DialogTrigger>
                      {selected?.id === o.id && <OrderDetail order={selected} onStatus={(s) => updateStatus(o.id, s)} />}
                    </Dialog>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">No orders found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrderDetail({ order, onStatus }) {
  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-serif text-2xl">Order #{order.order_number}</DialogTitle></DialogHeader>
      <div className="space-y-5 pt-2">
        <div>
          <Select value={order.status} onValueChange={onStatus}>
            <SelectTrigger className="w-full rounded-sm" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="overline text-gold-dark mb-1">Customer</p>
            <p className="font-medium">{order.customer.name}</p>
            <p className="text-muted-foreground">{order.customer.mobile}</p>
            <p className="text-muted-foreground break-all">{order.customer.email}</p>
          </div>
          <div>
            <p className="overline text-gold-dark mb-1">Ship To</p>
            {order.shipping_address ? (
              <>
                <p className="text-muted-foreground">{order.shipping_address.address}</p>
                <p className="text-muted-foreground">{order.shipping_address.city}, {order.shipping_address.state}</p>
                <p className="text-muted-foreground">{order.shipping_address.pincode}</p>
              </>
            ) : (
              <p className="text-muted-foreground">No address (contact customer)</p>
            )}
          </div>
        </div>

        <div>
          <p className="overline text-gold-dark mb-2">Items</p>
          <div className="space-y-3">
            {order.items.map((it) => (
              <div key={it.product_id} className="flex gap-3">
                <img src={resolveImage(it.image)} alt="" className="w-12 h-14 object-cover bg-secondary" />
                <div className="flex-1"><p className="text-sm font-medium">{it.name}</p><p className="text-xs text-muted-foreground">{it.sku} · Qty {it.quantity} · {formatINR(it.price)}</p></div>
                <span className="text-sm font-semibold">{formatINR(it.line_total)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-black/10 pt-3 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Total MRP</span><span>{formatINR(order.subtotal_mrp)}</span></div>
          <div className="flex justify-between text-green-700"><span>Savings</span><span>- {formatINR(order.total_savings)}</span></div>
          <div className="flex justify-between font-bold text-lg"><span>Total</span><span>{formatINR(order.total)}</span></div>
          <div className="flex justify-between text-xs text-muted-foreground"><span>Payment</span><span>{order.payment_method} ({order.payment_status})</span></div>
        </div>
      </div>
    </DialogContent>
  );
}
